"use client";
import { Modal } from "@/components/atoms/modal";

import { cn } from "@/lib/utils";
import { GotoPassInfoLabelQuery } from "@/types/generated";
import { XIcon } from "lucide-react";
import Image from "next/image";
import { useEffect, useState } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { IUserInfo } from "./GlobalFooter";
import { iconList } from "@/assets/icons";
import { DialogTitle } from "@radix-ui/react-dialog";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import Link from "next/link";

export default function UserInfoModal({
  userInfo,
  isOpen,
  setIsOpen,
  point,
}: Readonly<{
  userInfo?: GotoPassInfoLabelQuery;
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  point?: IUserInfo;
}>) {
  const { Spinner } = iconList;

  const [currentIcon, setCurrentIcon] = useState("");
  const userInfomation =
    userInfo?.sharedDatasourceGotopassParticularPages[0]?.user_info;

  useEffect(() => {
    if (userInfomation && point?.rank) {
      const rankKey =
        `${point?.rank?.toLowerCase()}_rank_icon` as keyof typeof userInfomation;
      const iconUrl = userInfomation[rankKey] as {
        cloudflare_url?: string | null;
        url?: string | null;
      };
      if (iconUrl && (iconUrl.cloudflare_url || iconUrl.url)) {
        //  Setting icon URL based on external user data from API
        // This is necessary as the icon URL is not available during initial render
        // eslint-disable-next-line react-hooks/set-state-in-effect -- Synchronizing icon state with external API data
        setCurrentIcon(iconUrl.cloudflare_url || iconUrl.url || "");
      }
    }
  }, [point?.rank, userInfomation]);

  if (!userInfo) {
    return null;
  }


  return (
    <Modal
      containerClassName="w-full md:w-[266px] md:w-90"
      open={isOpen}
      toggleModal={() => setIsOpen(false)}
      footerClassName="hidden"
      headerClassName="hidden h-0"
      header={<></>}
      trigger
    >
      <DialogTitle>
        <VisuallyHidden>
          {
            userInfo.sharedDatasourceGotopassParticularPages[0]?.user_info
              ?.goto_pass_label
          }
        </VisuallyHidden>
      </DialogTitle>
      <div
        className={cn(
          "flex h-full flex-col overflow-hidden rounded-lg",
          "transition-shadow",
        )}
      >
        <div className="flex flex-col gap-2">
          {point && (
            <>

              <div className="flex items-center gap-1 border-b border-[var(--stroke-gray-light)] p-4 pt-0">
                <p className="text-2xl font-[var(--font-semibold)]">
                  {
                    userInfo.sharedDatasourceGotopassParticularPages[0]
                      ?.user_info?.goto_pass_label
                  }
                </p>
                <div className="relative h-6 w-6">
                  <Image
                    fill
                    object-fit="cover"
                    src={
                      userInfo.sharedDatasourceGotopassParticularPages[0]
                        ?.user_info?.goto_pass_icon?.cloudflare_url ||
                      userInfo.sharedDatasourceGotopassParticularPages[0]
                        ?.user_info?.goto_pass_icon?.url ||
                      ""
                    }
                    alt={""}
                  />
                </div>
                {/* <p className="pl-[34px] text-base font-[var(--font-semibold)] text-[var(--text-gray-stronger)]">
                {point.status ?? ""}
              </p> */}
                <DialogPrimitive.Close
                  className={cn(
                    "absolute top-4 right-4 z-20 cursor-pointer rounded-full bg-[var(--fill-base-main)] p-1 transition-opacity hover:opacity-100 focus:outline-none disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-6",
                  )}
                >
                  <XIcon className="text-white" />
                </DialogPrimitive.Close>
              </div>
              <div className="flex flex-col gap-5 p-4">
                <div className="flex items-center gap-2">
                  <div className="relative h-[22px] w-[18px]">
                    <Image
                      src="/person.svg"
                      fill
                      alt=""
                      className="object-contain"
                    />
                  </div>
                  <p
                    className="text-2xl font-[var(--font-semibold)] text-[var(--text-gray-normal)]"
                    data-wovn-ignore
                  >
                    {point.name}
                  </p>
                </div>
                <div className="">
                  <p className="pb-4 text-base text-[var(--text-gray-normal)]">
                    {
                      userInfo.sharedDatasourceGotopassParticularPages[0]
                        ?.user_info?.point_available_label
                    }
                  </p>
                  <div className="flex w-full items-center justify-between px-4">
                    <p className="text-3xl text-[var(--text-gray-normal)]">
                      {point.point}
                    </p>
                    <div className="relative h-9 w-9">
                      <Image
                        src={
                          userInfo.sharedDatasourceGotopassParticularPages[0]
                            ?.user_info?.point_icon?.cloudflare_url ||
                          userInfo.sharedDatasourceGotopassParticularPages[0]
                            ?.user_info?.point_icon?.url ||
                          ""
                        }
                        alt={""}
                        fill
                        className="object-contain"
                      />
                    </div>
                  </div>
                </div>
                <div>
                  <p className="pb-4 text-base text-[var(--text-gray-normal)]">
                    {
                      userInfo.sharedDatasourceGotopassParticularPages[0]
                        ?.user_info?.ranking_label
                    }
                  </p>
                  <div className="flex w-full items-center justify-between px-4">
                    <p className="text-3xl text-[var(--text-gray-normal)]">
                      {point.rank}
                    </p>
                    <div className="relative h-[36px] w-[31px]">
                      <Image
                        src={currentIcon}
                        alt={""}
                        object-fit="cover"
                        fill
                      />
                    </div>
                  </div>
                </div>
              </div>
              <Link
                href={
                  userInfo.sharedDatasourceGotopassParticularPages[0]?.user_info
                    ?.button_link?.metadata?.text ||
                  userInfo.sharedDatasourceGotopassParticularPages[0]?.user_info
                    ?.button_link?.metadata?.url ||
                  ""
                }
                target={"_blank"}
                className="mx-[33px] mt-2 mb-2 flex h-10 items-center justify-center !bg-[var(--button-go-to-pass-bg)] text-white"
              >
                <p className="text-lg font-[var(--font-semibold)]">
                  {
                    userInfo.sharedDatasourceGotopassParticularPages[0]
                      ?.user_info?.currently_label
                  }
                </p>
                <div className="relative ml-2 h-5 w-5">
                  <Image
                    fill
                    object-fit="cover"
                    src={
                      userInfo.sharedDatasourceGotopassParticularPages[0]
                        ?.user_info?.icon?.cloudflare_url ||
                      userInfo.sharedDatasourceGotopassParticularPages[0]
                        ?.user_info?.icon?.url ||
                      ""
                    }
                    alt={""}
                  />
                </div>
              </Link>
            </>
          )}
          {/* Logout must use <a> tag for full page navigation to API route that clears auth cookies */}
          {/* eslint-disable-next-line @next/next/no-html-link-for-pages -- API route requires full page refresh */}
          <a
            className="mx-auto w-48 cursor-pointer rounded-b-lg py-2 text-center text-sm text-gray-800 underline"
            href="/api/auth/logout"
          >
            ログアウト
          </a>
        </div>
      </div>
    </Modal>
  );
}